import React, { useState } from "react";
import Mustache from "mustache";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { KTSVG } from "_metronic/helpers";

interface TemplateValues {
  [key: string]: string;
}

const validationSchema = Yup.object({
  subjectTemplate: Yup.string().required("Subject template is required"),
  bodyTemplate: Yup.string().required("Body template is required"),
});

export default function EmailTemplateEditor() {
  const [subjectTemplate, setSubjectTemplate] = useState("Welcome {{name}}");
  const [bodyTemplate, setBodyTemplate] = useState(
    "Hi {{name}},<br><br>Welcome to Video Jobfairs{{date}}.<br><br>Thanks,<br>Team"
  );
  const [values, setValues] = useState<TemplateValues>({ name: "", date: "" });

  const handleValueChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  // Extract unique mustache tags
  const extractPlaceholders = (template: string) => {
    const regex = /{{\s*([a-zA-Z0-9_]+)\s*}}/g;
    const tags = new Set<string>();
    let match;
    while ((match = regex.exec(template))) {
      tags.add(match[1]);
    }
    return Array.from(tags);
  };

  const placeholders = Array.from(
    new Set([
      ...extractPlaceholders(subjectTemplate),
      ...extractPlaceholders(bodyTemplate),
    ])
  );

  const renderedSubject = Mustache.render(subjectTemplate, values);
  const renderedBody = Mustache.render(bodyTemplate, values);

  return (
    <div className="card">
      <div className="card-header border-0 pt-6">
        <div className="card-title">
          <h3 className="fw-bolder">Email Template Editor</h3>
        </div>
        <div className="card-toolbar">
          <button type="button" className="btn btn-sm btn-light-primary me-3">
            <KTSVG path="/media/icons/duotune/arrows/arr075.svg" className="svg-icon-2" />
            Save Template
          </button>
        </div>
      </div>

      <div className="card-body py-4">
        <Formik
          initialValues={{
            subjectTemplate,
            bodyTemplate,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setSubjectTemplate(values.subjectTemplate);
            setBodyTemplate(values.bodyTemplate);
          }}
        >
          {({ errors, touched }) => (
            <Form className="form">
              <div className="row mb-6">
                <div className="col-lg-12">
                  <div className="fv-row mb-0">
                    <label className="form-label fs-6 fw-bolder text-dark">Subject Template</label>
                    <Field
                      name="subjectTemplate"
                      className={`form-control form-control-lg form-control-solid ${
                        errors.subjectTemplate && touched.subjectTemplate ? "is-invalid" : ""
                      }`}
                    />
                    {errors.subjectTemplate && touched.subjectTemplate && (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">{errors.subjectTemplate}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-6">
                <div className="col-lg-12">
                  <div className="fv-row mb-0">
                    <label className="form-label fs-6 fw-bolder text-dark">Body Template</label>
                    <Field
                      as="textarea"
                      name="bodyTemplate"
                      className={`form-control form-control-lg form-control-solid ${
                        errors.bodyTemplate && touched.bodyTemplate ? "is-invalid" : ""
                      }`}
                      rows={6}
                    />
                    {errors.bodyTemplate && touched.bodyTemplate && (
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">{errors.bodyTemplate}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="separator separator-dashed my-10"></div>

              <div className="row mb-6">
                <label className="form-label fs-6 fw-bolder text-dark">Placeholder Values</label>
                <div className="col-lg-12">
                  <div className="row g-4">
                    {placeholders.map((key) => (
                      <div key={key} className="col-md-6">
                        <input
                          type="text"
                          className="form-control form-control-solid"
                          placeholder={`Enter value for ${key}`}
                          value={values[key] || ""}
                          onChange={(e) => handleValueChange(key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="separator separator-dashed my-10"></div>

              <div className="row">
                <div className="col-lg-12">
                  <div className="card bg-light">
                    <div className="card-header">
                      <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bold text-dark">Preview</span>
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="fw-bold mb-2">Subject: {renderedSubject}</div>
                      <div className="separator separator-dashed my-5"></div>
                      <div dangerouslySetInnerHTML={{ __html: renderedBody }} />
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
